/**
 * Full integrity scan content for PDF export (all findings + playbook, not one-at-a-time UI).
 */
import FindingCalculationDetails from '../FindingCalculationDetails';
import { DEFAULT_INTRO, formatMoney, severityClass } from './accountingConstants';

const PDF_CONTEXT_MAX_CHARS = 8000;

function formatContextForPdf(context) {
  const json = JSON.stringify(context, null, 2);
  if (json.length <= PDF_CONTEXT_MAX_CHARS) {
    return json;
  }

  return `${json.slice(0, PDF_CONTEXT_MAX_CHARS)}\n… [truncated for PDF — open the app for full technical context]`;
}

function FindingBlock({ finding, index }) {
  const title = finding.plain_title || finding.title;
  const summary = finding.plain_summary || finding.description;

  return (
    <article className="accounting-audit-pdf-finding accounting-audit-pdf-chunk">
      <header className="accounting-audit-pdf-finding__head">
        <span className="accounting-audit-pdf-finding__num">{index + 1}.</span>
        <strong>{title}</strong>
        <span className={`admin-badge audit-severity ${severityClass(finding.severity)}`}>
          {finding.severity_label || finding.severity || 'info'}
        </span>
      </header>
      {summary && <p className="small mb-2">{summary}</p>}
      <FindingCalculationDetails details={finding.calculation_details} severity={finding.severity} />
      {finding.what_it_means && (
        <p className="small mb-1">
          <strong>What this means:</strong> {finding.what_it_means}
        </p>
      )}
      {finding.what_to_do && (
        <p className="small mb-1">
          <strong>What to do:</strong> {finding.what_to_do}
        </p>
      )}
      {finding.likely_cause && (
        <p className="small mb-1">
          <strong>Likely cause:</strong> {finding.likely_cause}
        </p>
      )}
      {(finding.suggested_fixes || []).length > 0 && (
        <ul className="small mb-2">
          {finding.suggested_fixes.map((fix) => (
            <li key={fix.fix_type}>
              {fix.plain_label || fix.label}
              {fix.plain_hint ? ` — ${fix.plain_hint}` : ''}
            </li>
          ))}
        </ul>
      )}
      {finding.context && (
        <pre className="audit-detail-pre accounting-audit-pdf-context">{formatContextForPdf(finding.context)}</pre>
      )}
    </article>
  );
}

export default function AccountingAuditFullReport({
  pumpId,
  pumpName,
  fromDate,
  toDate,
  scan,
}) {
  if (!scan) {
    return null;
  }

  const intro = scan.intro || DEFAULT_INTRO;
  const health = scan.health;
  const healthPlain = health?.plain;
  const playbook = scan.playbook;
  const sections = scan.sections || [];
  const findingTotal = sections.reduce((n, s) => n + (s.findings?.length || 0), 0);
  const generatedAt = new Date().toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });

  return (
    <div className="accounting-audit-pdf-root">
      <div className="accounting-audit-pdf-chunk">
        <header className="accounting-audit-pdf-cover">
          <h1>{pumpName || `Pump #${pumpId}`} — Accounting integrity audit</h1>
          <p className="accounting-audit-pdf-cover__meta">
            Period: {fromDate} to {toDate}
            {' · '}
            Generated: {generatedAt}
            {' · '}
            Pump ID: {pumpId}
          </p>
          {scan.pump_name && scan.pump_name !== pumpName && (
            <p className="small text-muted mb-0">{scan.pump_name}</p>
          )}
        </header>

        <section className="accounting-audit-pdf-section">
          <h2>{intro.title || 'Overview'}</h2>
          <p className="small">{intro.body}</p>
        </section>
      </div>

      {health && (
        <section className="accounting-audit-pdf-section accounting-audit-pdf-chunk">
          <h2>Account health summary</h2>
          <div className="accounting-audit-pdf-metrics">
            <div>
              <span className="text-muted small">Health score</span>
              <div className="fw-bold">{health.score} — {healthPlain?.score_message || health.label}</div>
            </div>
            {health.bs_pl && (
              <>
                <div>
                  <span className="text-muted small">{healthPlain?.bs_label || 'Balance sheet'}</span>
                  <div className="fw-bold">{formatMoney(health.bs_pl.bs_net)}</div>
                </div>
                <div>
                  <span className="text-muted small">{healthPlain?.pl_label || 'Profit & loss'}</span>
                  <div className="fw-bold">{formatMoney(health.bs_pl.pl_net)}</div>
                </div>
                <div>
                  <span className="text-muted small">{healthPlain?.diff_label || 'BS vs PL difference'}</span>
                  <div className="fw-bold">{formatMoney(health.bs_pl.difference)}</div>
                </div>
              </>
            )}
            <div>
              <span className="text-muted small">Must fix soon</span>
              <div className="fw-bold">{health.finding_counts?.critical ?? 0}</div>
            </div>
            <div>
              <span className="text-muted small">Review when you can</span>
              <div className="fw-bold">{health.finding_counts?.warning ?? 0}</div>
            </div>
          </div>
          <p className="small text-muted mb-0">Total findings in this report: {findingTotal}</p>
        </section>
      )}

      {playbook?.steps?.length > 0 && (
        <section className="accounting-audit-pdf-section accounting-audit-pdf-chunk">
          <h2>{playbook.title || 'Step-by-step fix guide'}</h2>
          <p className="small text-muted">{playbook.description}</p>
          <ol className="accounting-audit-pdf-playbook">
            {playbook.steps.map((step) => (
              <li key={step.step}>
                <strong>Step {step.step}: {step.plain_label || step.label}</strong>
                <div className="small text-muted">{step.plain_reason || step.reason}</div>
              </li>
            ))}
          </ol>
        </section>
      )}

      {(() => {
        let findingCounter = 0;
        return sections.map((section) => {
          const findings = section.findings || [];
          if (findings.length === 0) {
            return null;
          }

          return (
            <section key={section.id || section.label} className="accounting-audit-pdf-section">
              <div className="accounting-audit-pdf-chunk">
                <h2>{section.label || section.id}</h2>
                {section.description && <p className="small text-muted">{section.description}</p>}
              </div>
              {findings.map((finding, i) => {
                const index = findingCounter;
                findingCounter += 1;
                return (
                  <FindingBlock
                    key={`${section.id}-${finding.finding_type}-${finding.entity_id || i}`}
                    finding={finding}
                    index={index}
                  />
                );
              })}
            </section>
          );
        });
      })()}

      {findingTotal === 0 && (
        <section className="accounting-audit-pdf-section accounting-audit-pdf-chunk">
          <p className="mb-0">No problems found for {fromDate} to {toDate}.</p>
        </section>
      )}
    </div>
  );
}
